import React, { useMemo, useState } from 'react';
import { Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, useAdminGet } from '../../kit';
import {
  AutomatedToggle, EngTags, MODES, MODE_LABELS, ModePill, N, Pct, WINDOWS, WINDOW_LABELS, arr, num, pctText,
  rangeOf, rateOf, str,
} from '../dashboard/shared';
import { SECTION, Trigger, fmtIST, fmtShortIST } from './shared';

/**
 * MEMAC → Stats (owner, 2026-10-27: "campaign wise stats for views etc like
 * we do for other campaigns"): each trigger's sends in the period and what
 * happened to them — sent (manual / automatic), delivered, opened, clicked,
 * CTA clicks, unsubscribes — the numbers the Campaigns funnel and Email
 * Center → Dashboard use (email/stats_triggers; human engagement unless
 * "Include automated"; team addresses left out). "Recipients" lists that
 * trigger's sends one by one (email/stats_sends filtered to the trigger).
 * Reads only.
 */

const PRESETS = WINDOWS.filter(w => w !== 'custom');
const YES = <span className="text-green-700">✓</span>;
const NO = <span className="text-grayscale-900">—</span>;
const flag = (v: any) => (v === null || v === undefined ? <span className="text-grayscale-900">n/a</span> : v ? YES : NO);

const Stats: React.FC<{ triggers: Trigger[]; onOpen: (id: string) => void; stamp?: number }> = ({ triggers, onOpen, stamp = 0 }) => {
  const [win, setWin] = useState('30d');
  const [mode, setMode] = useState('all');
  const [automated, setAutomated] = useState(false);
  const [tick, setTick] = useState(0);
  const [picked, setPicked] = useState('');
  const range = rangeOf({ window: win });
  const params: Record<string, string> = { ...(range as Record<string, string>) };
  if (mode !== 'all') params.mode = mode;
  if (automated) params.automated = '1';
  const q = useAdminGet(SECTION, 'stats_triggers', params, [JSON.stringify(params), tick, stamp]);
  const d: any = q.data || {};
  const names = useMemo(() => Object.fromEntries(triggers.map(t => [t.id, t.name])), [triggers]);
  const rows = useMemo(() => arr(d.triggers).map((r: any) => ({
    ...r,
    trigger: str(r.trigger_id),
    name: names[str(r.trigger_id)] || '',
    open_rate: rateOf(r, 'open_rate'), click_rate: rateOf(r, 'click_rate'), ctor: rateOf(r, 'ctor'),
    cta_click_rate: rateOf(r, 'cta_click_rate'),
    cta: r.primary_cta?.label ? str(r.primary_cta.label) : '',
  })), [d.triggers, names]);
  const t = d.totals || {};

  return (
    <div className="flex flex-col gap-4">
      <Section title="Results by trigger"
        right={<div className="flex gap-2 items-center flex-wrap">
          <Select value={win} onChange={setWin} options={PRESETS} labels={WINDOW_LABELS} />
          <Select value={mode} onChange={setMode} options={MODES} labels={MODE_LABELS} />
          <AutomatedToggle value={automated} onChange={setAutomated} />
          <Btn small onClick={() => setTick(n => n + 1)}>Refresh</Btn>
        </div>}>
        <p className="text-[11px] text-grayscale-1100 -mt-1">
          Every MEMAC send in the period, by trigger: manual sends (“Send to these N now”) and automatic ones.
          Open rate = opened ÷ delivered over HTML mails; click rate = clicked ÷ delivered. Opens and clicks are
          people's unless “Include automated” is on. Team addresses are left out.
        </p>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading && !q.data ? <Loading /> : (
          <>
            <KPIRow>
              <KPI label="Triggers that sent" value={rows.length.toLocaleString()} />
              <KPI label="Sent" value={num(t.sent).toLocaleString()} />
              <KPI label="Delivered" value={num(t.delivered).toLocaleString()} />
              <KPI label="Open rate" value={<Pct v={rateOf(t, 'open_rate')} />} hint={`${num(t.opened).toLocaleString()} opened`} />
              <KPI label="Click rate" value={<Pct v={rateOf(t, 'click_rate')} />} hint={`${num(t.clicked).toLocaleString()} clicked`} />
              <KPI label="CTA clicks" value={t.cta_clicks === null || t.cta_clicks === undefined ? 'n/a' : num(t.cta_clicks).toLocaleString()}
                hint={t.cta_click_rate !== null && t.cta_click_rate !== undefined ? `${pctText(rateOf(t, 'cta_click_rate'))} of delivered` : undefined} />
              <KPI label="Unsubscribed" value={num(t.unsubscribed).toLocaleString()} />
            </KPIRow>
            <DataTable rows={rows} csvName="memac_trigger_stats" tableId="memac_trigger_stats" defaultSort="-sent"
              maxHeight="34rem"
              columns={['trigger', 'name', 'sent', 'delivered', 'opened', 'open_rate', 'clicked', 'click_rate',
                'cta_clicks', 'unsubscribed', 'last_sent_at']}
              optionalColumns={['manual', 'auto', 'ctor', 'cta', 'cta_click_rate', 'bounced', 'failed', 'auto_opened',
                'bot_clicked', 'first_sent_at']}
              labels={{ trigger: 'ID', name: 'Trigger', open_rate: 'Open rate', click_rate: 'Click rate',
                cta_clicks: 'CTA clicks', cta: 'Primary CTA', cta_click_rate: 'CTA click rate', ctor: 'CTOR',
                last_sent_at: 'Last sent', first_sent_at: 'First sent', auto_opened: 'Auto opens', bot_clicked: 'Bot clicks',
                manual: 'Manual', auto: 'Automatic' }}
              render={{
                trigger: r => (
                  <button type="button" onClick={() => onOpen(str(r.trigger_id))}
                    className="font-mono text-xs text-primary-700 hover:underline" title="Open this trigger">{r.trigger}</button>
                ),
                name: r => <span className="font-medium">{r.name || '—'}</span>,
                sent: r => (
                  <span title={`${num(r.manual)} manual · ${num(r.auto)} automatic`}>
                    <N v={r.sent} />
                    <span className="block text-[10px] text-grayscale-1100">
                      {num(r.auto) && num(r.manual) ? `${num(r.auto)} auto · ${num(r.manual)} manual`
                        : num(r.auto) ? 'automatic' : 'manual'}
                    </span>
                  </span>
                ),
                open_rate: r => <Pct v={r.open_rate} />,
                click_rate: r => <Pct v={r.click_rate} />,
                ctor: r => <Pct v={r.ctor} />,
                cta_click_rate: r => <Pct v={r.cta_click_rate} />,
                cta_clicks: r => (r.cta_clicks === null || r.cta_clicks === undefined
                  ? <span className="text-grayscale-1100" title="A mailto: button cannot be counted">n/a</span> : <N v={r.cta_clicks} />),
                last_sent_at: r => (r.last_sent_at ? <span title={fmtIST(r.last_sent_at)}>{fmtShortIST(r.last_sent_at)}</span> : '—'),
                first_sent_at: r => (r.first_sent_at ? <span title={fmtIST(r.first_sent_at)}>{fmtShortIST(r.first_sent_at)}</span> : '—'),
              }}
              actions={r => (
                <Btn small onClick={() => setPicked(p => (p === str(r.trigger_id) ? '' : str(r.trigger_id)))}>
                  {picked === str(r.trigger_id) ? 'Hide recipients' : 'Recipients'}
                </Btn>
              )}
              empty="No MEMAC trigger sent anything in this period." />
          </>
        )}
      </Section>
      {picked && (
        <Recipients triggerId={picked} name={names[picked] || ''} params={params}
          stamp={tick + stamp} onClose={() => setPicked('')} />
      )}
    </div>
  );
};

/** One trigger's sends, newest first (email/stats_sends, trigger_id). */
const Recipients: React.FC<{
  triggerId: string; name: string; params: Record<string, string>; stamp: number; onClose: () => void;
}> = ({ triggerId, name, params, stamp, onClose }) => {
  const p = { ...params, trigger_id: triggerId, per_page: '500' };
  const q = useAdminGet(SECTION, 'stats_sends', p, [JSON.stringify(p), stamp]);
  const d: any = q.data || {};
  const rows = arr(d.rows);
  return (
    <Section title={`Recipients — ${triggerId}${name ? ` · ${name}` : ''}`}
      right={<Btn small onClick={onClose}>Close</Btn>}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading && !q.data ? <Loading /> : (
        <>
          {num(d.total) > rows.length && (
            <div className="text-[11px] text-grayscale-1100">
              Showing the newest {rows.length.toLocaleString()} of {num(d.total).toLocaleString()} sends (download the CSV for these).
            </div>
          )}
          <DataTable rows={rows} csvName={`memac_${triggerId}_recipients`} tableId="memac_trigger_recipients"
            defaultSort="-at" maxHeight="30rem"
            columns={['at', 'email', 'name', 'mode', 'status', 'delivered', 'opened', 'clicked', 'cta_clicked']}
            optionalColumns={['subject', 'template_name', 'unsubscribed', 'eng_tags', 'error']}
            labels={{ at: 'Sent', cta_clicked: 'CTA clicked', template_name: 'Template', eng_tags: 'Automated' }}
            render={{
              at: r => (r.at ? <span title={fmtIST(r.at)}>{fmtShortIST(r.at)}</span> : '—'),
              mode: r => <ModePill mode={r.mode} />,
              delivered: r => flag(r.delivered),
              opened: r => (r.html === false ? <span className="text-grayscale-900" title="Plain-text mail: opens cannot be seen">n/a</span> : flag(r.opened)),
              clicked: r => flag(r.clicked),
              cta_clicked: r => flag(r.cta_clicked),
              unsubscribed: r => flag(r.unsubscribed),
              eng_tags: r => <EngTags row={r} />,
            }}
            empty="No send from this trigger in this period." />
        </>
      )}
    </Section>
  );
};

export default Stats;
